import { useEffect, useRef, useState } from 'react'
import { callAppsScript } from '@/lib/agents/sheets-tools'
import type { Platform } from '@/lib/platform'

const EXCEL_WORKBOOK_UUID_SETTING_KEY = 'pipedreamer_workbook_uuid'

async function getExcelWorkbookUuid(): Promise<string | null> {
  if (typeof Office === 'undefined' || !Office.context?.document?.settings) {
    return null
  }

  const existing = Office.context.document.settings.get(
    EXCEL_WORKBOOK_UUID_SETTING_KEY
  )
  if (typeof existing === 'string' && existing.trim().length > 0) {
    return existing
  }

  const created = crypto.randomUUID()
  Office.context.document.settings.set(EXCEL_WORKBOOK_UUID_SETTING_KEY, created)
  await new Promise<void>((resolve) => {
    Office.context.document.settings.saveAsync(() => resolve())
  })
  return created
}

async function getSheetsActiveIds(): Promise<{
  spreadsheetId: string
  sheetId: number
} | null> {
  try {
    const result = await callAppsScript<{
      success: boolean
      spreadsheetId: string
      sheetId: number
    }>('getActiveSpreadsheetAndSheetIds')

    if (!result?.success) return null
    if (!result.spreadsheetId || typeof result.spreadsheetId !== 'string')
      return null
    if (typeof result.sheetId !== 'number') return null
    return { spreadsheetId: result.spreadsheetId, sheetId: result.sheetId }
  } catch (error) {
    console.warn('[workbook-key] Failed to get Sheets IDs', error)
    return null
  }
}

export async function resolveWorkbookKey(
  platform: Platform
): Promise<string | null> {
  if (platform === 'excel') {
    const workbookUuid = await getExcelWorkbookUuid()
    if (!workbookUuid) return null
    return `excel:${workbookUuid}`
  }

  const ids = await getSheetsActiveIds()
  if (!ids) return null
  return `google_sheets:${ids.spreadsheetId}`
}

/**
 * Returns a stable per-workbook key:
 * - Excel: excel:<workbookUuid>
 * - Sheets: google_sheets:<spreadsheetId>
 */
export function useWorkbookKey(platform: Platform): string | null {
  const [workbookKey, setWorkbookKey] = useState<string | null>(null)
  const workbookKeyRef = useRef<string | null>(null)
  const updateInFlightRef = useRef<Promise<string | null> | null>(null)
  const isMountedRef = useRef(true)
  const retryTimeoutRef = useRef<number | null>(null)
  const retryAttemptRef = useRef(0)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    let disposed = false

    const refresh = async () => {
      // If a refresh is already running, await it (don't start another).
      if (updateInFlightRef.current) {
        await updateInFlightRef.current
        return
      }

      updateInFlightRef.current = (async (): Promise<string | null> => {
        try {
          return await resolveWorkbookKey(platform)
        } catch (error) {
          console.warn('[workbook-key] Failed to resolve workbook key', error)
          return null
        }
      })()

      const next = await updateInFlightRef.current
      updateInFlightRef.current = null

      if (disposed || !isMountedRef.current) return

      if (next && next !== workbookKeyRef.current) {
        workbookKeyRef.current = next
        setWorkbookKey(next)
        retryAttemptRef.current = 0
      }

      if (!next) {
        // Keep behavior: if we previously had a key, clear it.
        if (workbookKeyRef.current !== null) {
          workbookKeyRef.current = null
          setWorkbookKey(null)
        }

        // Sheets runs inside an Apps Script iframe. The postMessage bridge can be
        // briefly unavailable during initial load, so retry with backoff.
        if (platform === 'sheets' && retryTimeoutRef.current === null) {
          const attempt = retryAttemptRef.current
          retryAttemptRef.current = Math.min(attempt + 1, 8)
          const delayMs = Math.min(250 * 2 ** attempt, 8000)
          retryTimeoutRef.current = window.setTimeout(() => {
            retryTimeoutRef.current = null
            void refresh()
          }, delayMs)
        }
      }
    }

    // Clear any prior retry when platform changes.
    retryAttemptRef.current = 0
    if (retryTimeoutRef.current !== null) {
      window.clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }

    void refresh()

    return () => {
      disposed = true
      if (retryTimeoutRef.current !== null) {
        window.clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [platform])

  return workbookKey
}
